'use client';

import InlineStyle from '@/components/InlineStyle';
import { motion } from 'framer-motion';
import { Activity, BarChart3, Clock, MessageSquareWarning, Radar, ShieldCheck, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { Button, Card, CheckList, FaqAccordion, PageHero, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BookDemoSection from '@/components/BookDemoSection';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';

// AUTO-GENERATED by the on-brand page generator. Real components only.
const ICONS = [Radar, Clock, Activity, MessageSquareWarning, TrendingUp, ShieldCheck, Sparkles, Target, BarChart3, Zap];
const METRICS = [{"value":"30%","label":"Reduction in customer churn"},{"value":"25%","label":"Increase in customer satisfaction"},{"value":"90%","label":"Accuracy in predictive analytics"}];
const PROBLEMS = ["Inability to identify at-risk customers","Lack of personalized customer experiences","Inefficient customer feedback mechanisms","Insufficient data-driven decision making"];
const CAPABILITIES = [{"title":"Predictive Analytics","body":"Identify at-risk customers and proactively engage with them to prevent churn"},{"title":"Personalized Customer Experiences","body":"Deliver tailored experiences that meet the unique needs and preferences of each customer"},{"title":"Customer Feedback Mechanisms","body":"Collect and analyze customer feedback to identify areas for improvement and optimize customer experiences"},{"title":"Data-Driven Decision Making","body":"Make informed decisions with data-driven insights and predictive analytics"},{"title":"Omnichannel Engagement","body":"Engage with customers across multiple channels and touchpoints to provide seamless experiences"},{"title":"Real-Time Insights","body":"Access real-time insights and analytics to respond quickly to changing customer needs and preferences"}];
const FAQS = [{"q":"What is AI-powered customer experience analytics?","a":"AI-powered customer experience analytics refers to the use of artificial intelligence and machine learning to analyze customer data and provide insights that can be used to improve customer experiences and reduce churn."},{"q":"How can I reduce customer churn with AI-powered customer experience analytics?","a":"You can reduce customer churn by using AI-powered customer experience analytics to identify at-risk customers, deliver personalized experiences, and optimize customer feedback mechanisms."},{"q":"What are the benefits of using AI-powered customer experience analytics?","a":"The benefits of using AI-powered customer experience analytics include improved customer satisfaction, reduced churn, and increased revenue."},{"q":"Is AI-powered customer experience analytics easy to implement?","a":"Yes, our platform is designed to be easy to implement and integrate with existing systems, allowing you to quickly start seeing the benefits of AI-powered customer experience analytics."}];

export default function GeneratedBody() {
  return (
    <div>
      <span className="sr-only">{"Discover how telecom operators can reduce customer churn with AI-powered customer experience analytics. Learn more and start your journey today."}</span>

      {/* 1 — Hero */}
      <PageHero
        align="center"
        eyebrow={"Transform Your Customer Experience"}
        title={<>{"Unlock the power of "}<span className="serif-accent italic">{"_Intelligent_"}</span>{" customer insights"}</>}
        subtitle={"Stay ahead of the competition with data-driven decision making and personalized customer experiences"}
        actions={<Button variant="cta" size="lg" href="#book-demo">{"Get Started"}</Button>}
      />

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={3} />

      {/* 3 — Problem / solution */}
      <PageSection bg="tint">
        <div className="lp-split">
          <SectionTitle
            align="left"
            eyebrow={"The Challenge of Customer Churn"}
            title={<>{"Are you struggling with "}<span className="serif-accent italic">{"_High_"}</span>{" customer churn rates?"}</>}
            subtitle={"Telecom operators face significant challenges in reducing customer churn, resulting in lost revenue and damaged brand reputation"}
          />
          <Card padding="xl" radius="2xl">
            <CheckList items={PROBLEMS} />
          </Card>
        </div>
      </PageSection>

      {/* 4 — Capability cards */}
      <PageSection bg="page">
        <SectionTitle
          align="center"
          eyebrow={"AI-Powered Customer Experience Capabilities"}
          title={<>{"Unlock the power of "}<span className="serif-accent italic">{"_AI_"}</span>{"-driven customer experience analytics"}</>}
          subtitle={"Our platform provides telecom operators with the tools and insights needed to reduce customer churn and improve customer satisfaction"}
        />
        <motion.div className="lp-grid" variants={staggerContainer(0.08)} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {CAPABILITIES.map((cap, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div key={cap.title} variants={fadeUp}>
                <Card padding="xl" radius="2xl" hover className="lp-card">
                  <span className="lp-icon" aria-hidden="true">
                    <Icon size={22} color="var(--brand)" strokeWidth={2.2} />
                  </span>
                  <h3 className="lp-card-title">{cap.title}</h3>
                  <p className="lp-card-body">{cap.body}</p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </PageSection>

      {/* 5 — FAQ */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle align="center" title={<>{"Frequently Asked "}<span className="serif-accent italic">{"_Questions_"}</span>{""}</>} />
        <FaqAccordion items={FAQS} columns={2} name="generated-faq" />
      </PageSection>

      {/* 6 — Book a demo (always last) */}
      <BookDemoSection />

      <InlineStyle css={`
        .lp-split { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        .lp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lp-card { height: 100%; }
        .lp-icon { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: var(--radius-lg); background: var(--brand-subtle); border: 1px solid var(--brand-border); margin-bottom: 16px; }
        .lp-card-title { font-size: 19px; font-weight: 700; color: var(--text-heading, #0D1B3E); margin: 0 0 8px; letter-spacing: -0.01em; }
        .lp-card-body { font-size: 14.5px; line-height: 1.65; color: var(--text-body); margin: 0; }
        @media (max-width: 900px) { .lp-split { grid-template-columns: 1fr; gap: 32px; } .lp-grid { grid-template-columns: 1fr; } }
      `} />
    </div>
  );
}
